// Organization API client

import apiClient from './client';
import {
  Organization,
  OrganizationStats,
  Member,
  Invite,
  CreateOrganizationRequest,
  GenerateInviteRequest,
  UseInviteRequest,
  TransferOwnershipRequest,
  MemberRole
} from '../types/organization';

// Organization Management

export async function createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
  const response = await apiClient.post<Organization>('/organizations', data);
  return response.data;
}

export async function getOrganization(id: string): Promise<Organization> {
  const response = await apiClient.get<Organization>(`/organizations/${id}`);
  return response.data;
}

export async function getUserOrganizations(): Promise<Organization[]> {
  const response = await apiClient.get<Organization[]>('/users/me/organizations');
  return response.data;
}

export async function updateOrganization(id: string, data: { name: string }): Promise<Organization> {
  const response = await apiClient.put<Organization>(`/organizations/${id}`, data);
  return response.data;
}

export async function deleteOrganization(id: string): Promise<void> {
  await apiClient.delete(`/organizations/${id}`);
}

export async function getOrganizationStats(id: string): Promise<OrganizationStats> {
  const response = await apiClient.get<OrganizationStats>(`/organizations/${id}/stats`);
  return response.data;
}

// Member Management

export async function getMembers(organizationId: string): Promise<Member[]> {
  const response = await apiClient.get<Member[]>(`/organizations/${organizationId}/members`);
  return response.data;
}

export async function getMember(organizationId: string, userId: string): Promise<Member> {
  const response = await apiClient.get<Member>(`/organizations/${organizationId}/members/${userId}`);
  return response.data;
}

export async function removeMember(organizationId: string, userId: string): Promise<void> {
  await apiClient.delete(`/organizations/${organizationId}/members/${userId}`);
}

export async function leaveOrganization(organizationId: string): Promise<void> {
  await apiClient.post(`/organizations/${organizationId}/leave`);
}

export async function transferOwnership(organizationId: string, data: TransferOwnershipRequest): Promise<void> {
  await apiClient.post(`/organizations/${organizationId}/transfer-ownership`, data);
}

export async function updateMemberRole(organizationId: string, userId: string, role: MemberRole): Promise<void> {
  await apiClient.patch(`/organizations/${organizationId}/members/${userId}/role`, { role });
}

// Invite Management

export async function generateInvite(organizationId: string, data: GenerateInviteRequest): Promise<Invite> {
  const response = await apiClient.post<Invite>(`/organizations/${organizationId}/invites`, data);
  return response.data;
}

export async function getInvites(organizationId: string): Promise<Invite[]> {
  const response = await apiClient.get<Invite[]>(`/organizations/${organizationId}/invites`);
  return response.data;
}

export async function validateInvite(code: string): Promise<Invite> {
  const response = await apiClient.get<Invite>(`/invites/${code}/validate`);
  return response.data;
}

export async function useInvite(data: UseInviteRequest): Promise<{ message: string; organization: Organization }> {
  const response = await apiClient.post<{ message: string; organization: Organization }>('/invites/use', data);
  return response.data;
}

export async function revokeInvite(organizationId: string, inviteId: string): Promise<void> {
  await apiClient.delete(`/organizations/${organizationId}/invites/${inviteId}`);
}

// Error handling helper
export function getErrorMessage(error: unknown): string {
  // Handle APIError from our client
  if (error && typeof error === 'object' && 'error' in error) {
    return (error as { error: string }).error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Неизвестная ошибка';
}
